<template>
  <div style="height: 100%; width: 100%;">
    <owal-panel style="margin: 2.5rem 5rem 0rem 5rem;" title="Alert Configuration Panel">
      <div class="alert-settings-panel__top-button-ribbon" style="width: 100%;">
        <owal-button-toggle v-if="hasFullEditing" :segments="buttonSegments" @change="setActiveConfig"/>
        <owal-button @click="saveChanges" style="margin-bottom: 1rem;" title="Save Changes" id="save-button"/>
      </div>
      <div class="alert-settings-panel__config-container">
        <div class="alert-settings-panel__master-view">
          <div
            v-for="(item, index) in masterList"
            :key="item.key"
            @click="setMasterKey(index)"
            class="alert-settings-panel__config-button"
            :style="`${index === configurationItemIndex ? '' : 'border-right: 1px solid rgb(188, 188, 188)'}`">
            {{item.description}}
            </div>
        </div>
        <div class="alert-settings-panel__detail-view">
          <camera-settings-view v-if="this.activeConfig === 'camera-view'" @change="processChange"/>
          <alert-settings-view v-if="this.activeConfig === 'alert-view'" @change="processChange"/>
        </div>
      </div>
    </owal-panel>
  </div>
</template>

<script>
import { OwalPanel, OwalButtonToggle, OwalButton } from '@/libraries/owal-ui/'
import CameraSettingsView from './CameraSettingsView'
import AlertSettingsView from './AlertSettingsView'
import { patchDNAConfig } from '@/libraries/api/dna'
import { mapGetters, mapActions, mapMutations, mapState } from 'vuex'
import { daysToIntString } from './dnaFormHelper'
import { Operation, Service } from '@/libraries/permissions'

export default {
  name: 'DNAConfigurationPanel',
  components: {
    OwalPanel,
    OwalButtonToggle,
    CameraSettingsView,
    AlertSettingsView,
    OwalButton,
  },
  data () {
    return {
      activeConfig: 'camera-view',
      buttonSegments: [{
        title: 'Cameras',
        icon: 'video',
        id: 'camera-view',
      },
      {
        title: 'Alerts',
        icon: 'bell',
        id: 'alert-view',
      }],
      configurationItemIndex: undefined,
      changes: {},
    }
  },
  methods: {
    ...mapActions({
      loadDomainConfiguration: 'dnaConfiguration/loadDomainConfiguration',
    }),
    ...mapMutations({
      clear: 'dnaConfiguration/clear',
      setCurrentCameraUUID: 'dnaConfiguration/setCurrentCameraUUID',
      setAlerts: 'dnaConfiguration/setAlerts',
      removeAlertById: 'dnaConfiguration/removeAlertById',
      setNotifications: 'dnaConfiguration/setNotifications',
      setConditions: 'dnaConfiguration/setConditions',
      setCameraDetectables: 'dnaConfiguration/setCameraDetectables',
      setCameras: 'dnaConfiguration/setCameras',
      setCurrentAlertId: 'dnaConfiguration/setCurrentAlertId',
    }),
    saveChanges () {
      // processes stored changes into DB friendly format and sends to API for handling
      const mappedForAPI = Object.values(this.changes).reduce(function (prev, curr) {
        if (curr.group === 'alerts') {
          const cameras = [ ...curr.values.cameras ]
          cameras.unshift('uuids')

          const conditions = curr.values.conditions.map(id => {
            const condition = { ...this.conditionsById[id] }
            condition.id = this.detectableForDescription(condition.detectableDescription).id
            if (id.match(/NEW-(\d*)$/)) {
              condition.parent_id = null
            } else {
              condition.parent_id = id
            }
            condition.max = parseInt(condition.max)
            condition.min = parseInt(condition.min)
            return condition
          })

          const notifications = [ ...curr.values.notifications ].map(id => {
            const notification = { ...this.notificationsById[id] }
            if (notification.type === 'slack' && typeof notification.info === 'string') {
              notification.info = { 'channel': notification.info }
            } else if (notification.type === 'email' && typeof notification.info === 'string') {
              notification.info = { 'to': notification.info }
            } else if (notification.type === 'teams' && typeof notification.info === 'string') {
              notification.info = { 'webhook': notification.info }
            }
            // isNaN([]) === false, so we also look for an empty array
            if (isNaN(notification.activeDays) || notification.activeDays.length < 1) {
              notification.activeDays = daysToIntString(notification.activeDays)
            }
            notification.startOffset = parseInt(notification.startOffset)
            notification.endOffset = parseInt(notification.endOffset)
            let parentId
            if (id.match(/NEW-(\d*)$/)) {
              parentId = null
            } else parentId = id
            return { ...notification, parent_id: parentId }
          })

          let alertId
          if (curr.parentId[1].match(/NEW-(\d*)$/)) {
            alertId = null
          } else alertId = curr.parentId[1]

          prev.push({ type: curr.group, values: { ...curr.values, parent_id: alertId, cameras, notifications, conditions } })
        } else if (curr.group === 'cameras') {
          const detectables = [ ...curr.values.detectables ].map(id => {
            const detectable = { ...this.cameraDetectablesById[id] }
            detectable.id = this.detectableForDescription(detectable.detectable).id
            detectable.retention = parseInt(detectable.retention)
            detectable.confidence = parseFloat(detectable.confidence)
            if (id.match(/NEW-(\d*)$/)) {
              detectable.parent_id = null
            } else detectable.parent_id = id
            return detectable
          })

          prev.push({ type: curr.group, values: { ...curr.values, parent_id: curr.parentId[1], detectables } })
        } else if (curr.group === 'deactivate') {
          if (!curr.parentId[1].match(/NEW-(\d*)$/)) {
            // only attempt to delete if config already exists
            prev.push({ type: curr.group, key: curr.parentId[0], id: curr.parentId[1] })
          }
        }
        return prev
      }.bind(this), [])
      patchDNAConfig({ changes: mappedForAPI, domain_uuid: this.currentDomainUUID }).catch(err => {
        // TODO: this endpoint doesn't error correctly, and I haven't been able to figure out why
        window.alert(err)
        throw err
      }).then(() => {
        this.changes = {}
        this.loadDomainConfiguration(true)
      })
    },
    processChange (change) {
      // stores changes and sets new data in store to ensure it's reactively
      // changes tracked are the high level camera and alert objects, with
      // subcomponents updated in store
      switch (change.group) {
        case ('conditions'):
          this.setConditions({ [change.parentId[1]]: change.values })
          break
        case ('notifications'):
          // grab previous values to carry over target_id and schedule_id
          const existingFields = { ...this.notificationsById[change.parentId[1]] }
          this.setNotifications({ [change.parentId[1]]: {
            ...change.values,
            target_id: existingFields.target_id,
            schedule_id: existingFields.schedule_id,
          } })
          break
        case ('detectables'):
          this.setCameraDetectables({ [change.parentId[1]]: change.values })
          break
        case ('cameras'):
          this.setCameras({ [change.parentId[1]]: change.values })
          this.changes[`${change.parentId[1]}-${change.group}`] = change
          break
        case ('alerts'):
          change.values.cameras = Object.entries(this.camerasById).map(function (camera) {
            // a change to the cameras field is returns array of descriptions, whereas other changes are an array of UUIDs (from store)
            if (change.values.cameras.includes(camera[1].description) || change.values.cameras.includes(camera[1].uuid)) {
              return camera[0]
            }
          }).filter(uuid => uuid !== undefined)
          this.setAlerts({ [change.parentId[1]]: change.values })
          this.changes[`${change.parentId[1]}-${change.group}`] = change
          break
        case ('deactivate'):
          if (change.parentId[0] === 'alert') {
            this.removeAlertById(change.parentId[1])
            this.setCurrentAlertId(undefined)
          }
          this.changes[`${change.parentId[1]}-${change.parentId[0]}s`] = change
          break
        default:
          break
      }
    },
    setActiveConfig (newIndex) {
      this.configurationItemIndex = undefined
      this.activeConfig = this.buttonSegments[newIndex].id
    },
    setMasterKey (index) {
      this.configurationItemIndex = index
      const key = this.masterList[index].key
      if (this.activeConfig === 'camera-view') {
        this.setCurrentCameraUUID(key)
      } else if (this.activeConfig === 'alert-view') {
        this.setCurrentAlertId(key)
      }
    },
  },
  computed: {
    ...mapGetters({
      currentDomainUUID: 'currentDomainUUID',
      detectableForDescription: 'dnaConfiguration/detectableForDescription',
      canPerformOperationForService: 'entitlements/canPerformOperationForService',
      currentDomainEntitlements: 'entitlements/currentDomainEntitlements',
    }),
    ...mapState('dnaConfiguration', {
      camerasById: state => state.camerasById,
      alertsById: state => state.alertsById,
      notificationsById: state => state.notificationsById,
      conditionsById: state => state.conditionsById,
      detectables: state => state.detectables,
      cameraDetectablesById: state => state.cameraDetectablesById,
    }),
    hasOnlyLimitedEditing () {
      return this.canPerformOperationForService(Service.ALERTING, Operation.LIMITED_EDIT) && !this.canPerformOperationForService(Service.ALERTING, Operation.EDIT)
    },
    hasFullEditing() {
      return this.canPerformOperationForService(Service.ALERTING, Operation.EDIT)
    },
    masterList () {
      if (this.activeConfig === 'camera-view') {
        return Object.entries(this.camerasById).map(camera => {
          return {
            key: camera[0],
            description: camera[1].description,
          }
        })
      } else if (this.activeConfig === 'alert-view') {
        return Object.entries(this.alertsById).map(alert => {
          return {
            key: alert[0],
            description: alert[1].description,
          }
        })
      } else return []
    },
  },
  mounted () {
    if (this.hasOnlyLimitedEditing) {
      this.activeConfig = 'alert-view'
    }
    this.loadDomainConfiguration()
  },
  watch: {
    currentDomainUUID () {
      this.clear()
      this.loadDomainConfiguration()
    },
  },
}
</script>

<style>
  .alert-settings-panel__detail-view {
    width: 100%;
    margin-top: -2.5rem;
    margin-left: 1.5rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    max-height: calc(100vh - 250px);
    overflow-x: auto;
  }

  .credential-realtime-view__banner-container {
    margin: 1rem;
    display: flex;
    flex-direction: row;
    width: 95%;
  }

  .alert-settings-panel__master-view {
    min-width: max-content;
    overflow-y: auto;
  }

  .alert-settings-panel__config-container {
    height: calc(100vh - 16rem);
    display: flex;
    flex-direction: row;
  }

  .credential-realtime-view__detection-details {
    margin-left: 1.5rem;
  }

  .alert-settings-panel__config-button {
    cursor: pointer;
    padding-right: 1.5rem;
    border-bottom: 1px solid rgb(188, 188, 188);
  }
</style>
